import React from "react";
import { Link } from "react-router-dom";
import { Phone, ArrowUpRight } from "lucide-react";

const PRECARE = [
  { title: "Fast for 3 hours before your appointment", detail: "A light meal 2–3 hours before is fine — please don't arrive fully full." },
  { title: "Shower and clean the skin right before", detail: "No lotions, perfumes, or oils on the treatment areas." },
  { title: "Avoid blood thinners for 48 hours", detail: "No aspirin, ibuprofen (Motrin/Aleve), or alcohol in the 48 hours before your session." },
  { title: "Shave hair if needed so the cups seal properly", detail: "Smooth skin lets the cups grip evenly and comfortably." },
  { title: "Wear loose, comfy clothing", detail: "Something soft that gives easy access to the treatment area." },
  { title: "Hydrate well in the 24 hours before", detail: "Drink plenty of water — well-hydrated skin responds best." },
];

const POSTCARE = [
  { title: "Do not shower for 24 hours", detail: "Keep the incisions dry so they can close and seal properly." },
  { title: "Avoid swimming, hot tubs, saunas and direct sunlight for 48 hours", detail: "Give the skin a calm environment to heal." },
  { title: "Apply black seed oil, olive oil, or antibacterial cream 2–3× daily", detail: "Gentle circular motions to soothe and support healing." },
  { title: "Don't scratch the skin — even if it itches", detail: "Itching is normal as the skin repairs; a light tap or cool compress helps." },
  { title: "Skip strenuous exercise for 24 hours", detail: "Rest lets your body direct energy toward healing." },
  { title: "Avoid red meat, dairy, processed sugar, and caffeine for 24–48 hours", detail: "Lighter, plant-forward meals help you feel your best." },
  { title: "Rest, stay warm, and keep cupped areas covered", detail: "Cozy clothing over the treated zones for the first day." },
];

function CarePage({ kind }) {
  const isPre = kind === "pre";
  const items = isPre ? PRECARE : POSTCARE;
  const title = isPre ? "Wet Cupping — Pre-Care Instructions" : "Wet Cupping — After-Care Instructions";
  const kicker = isPre ? "Before your appointment" : "After your appointment";
  const intro = isPre
    ? "A few gentle steps to prepare so your session is safe, comfortable, and effective."
    : "Simple ways to take care of your body as it heals in the first 24–48 hours.";

  return (
    <main data-testid={`care-${kind}-page`} className="pt-28 pb-28 min-h-screen relative z-10">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <p className="overline text-[#9A7420] mb-3">{kicker}</p>
        <h1 className="font-display text-4xl md:text-6xl tracking-tight mb-4 leading-[1.05]">{title}</h1>
        <p className="text-[#57515F] mb-14 text-base md:text-lg">{intro}</p>

        <ol className="space-y-6">
          {items.map((it, i) => (
            <li key={i} className="flex gap-5 border-t border-[#DAD5E2] pt-6">
              <span className="font-display text-3xl text-[#B0872A] leading-none shrink-0 w-10">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div>
                <h3 className="font-body font-medium text-lg text-[#2B2340] mb-1">{it.title}</h3>
                <p className="text-[#57515F] text-sm md:text-base leading-relaxed">{it.detail}</p>
              </div>
            </li>
          ))}
        </ol>

        <div className="mt-14 p-6 bg-[#F8F5EE] border border-[#E4CE8F]/60 rounded-2xl">
          <p className="overline text-[#9A7420] mb-2">Questions or concerns?</p>
          <p className="text-sm text-[#57515F] mb-4">
            Please call the spa if anything feels off — we&apos;re here for you.
          </p>
          <div className="flex flex-wrap gap-3">
            <a
              href="tel:+16616668041"
              data-testid={`care-call-${kind}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#2B2340] text-white text-sm font-medium"
            >
              <Phone className="w-4 h-4" strokeWidth={2} /> Call (661) 666-8041
            </a>
            <Link
              to="/consultation"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#2B2340] text-[#2B2340] text-sm font-medium"
            >
              Book another session <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
            </Link>
          </div>
        </div>

        <p className="text-xs text-[#8B8494] text-center mt-16">
          Cosmetic guidance only — not a medical diagnosis. Individual results vary.
        </p>
      </div>
    </main>
  );
}

export function WetCuppingPreCare() { return <CarePage kind="pre" />; }
export function WetCuppingPostCare() { return <CarePage kind="post" />; }
